
import AppBankEdit from "@/components/bank/QuickEdit.vue";
import AppBrowse from "@/components/browse/Browse.vue";
import AppSaleReminder from "@/components/sales/ReminderSend.vue";
import AppSendSale from "@/components/sales/SaleSendModal.vue";
import { Sale } from "@/models/base";
import { BrowseService } from "@/services/browse-service";
import { DossierConfigService } from "@/services/dossier-config-service";
import { SaleService } from "@/services/sale-service";
import { ToolbarService } from "@/services/toolbar-service";
import { IBrowseDataSource } from "@/utils/browse/browse-data-source";
import { BrowseState } from "@/utils/browse/browse-state";
import Vue from "vue";
import { ISelectItem } from "winfakt-vue-components";
import { UserConfigService } from "../../services/user-config-service";
import { UblService } from "@/plugins/ubl/services/ubl-service";
import { AuthService } from "../../services/auth-service";
import { MODULE } from "../../config/modules";
import { View } from "@/models/view";
import AppPublicLink from "@/components/PublicLink.vue";
import AppSendPortal from "@/components/client-portal/PortalSendModal.vue";
import AppCreateUblArchiveModal from "@/components/settings-default/CreateUblArchiveModal.vue";
import { checkHasModule } from "@/router";
import { FetchedTotal } from "@/utils/views/fetched-total";
import AppSaveAsModal from "@/components/sales/SaveAsModal.vue";
import AppConvertToCreditNoteModal from "@/components/sales/ConvertToCreditNoteModal.vue";
import { JobService } from "@/services";
import AppSendViaPeppolModal from "@/components/sales/SendViaPeppolModal.vue";

export default Vue.extend({
	data() {
		return {
			quickBankEditSale: null as null | Sale,
			saleReminder: false,
			sendModal: false,
			sendPortalModal: false,
			toSendSale: null as Sale | null,
			toSendEmailAddress: "",
			publicLinkModal: false,
			publicLink: "",
			browseState: null as null | BrowseState,
			selectedItems: [] as Sale[],
			createUblArchiveModal: false,
			saveAsModal: false,
			editDeliveryDateModalSale: null as null | Sale,
			peppolModalOpen: false,
			convertToCreditNoteModal: false,
			selectedContextMenuItem: null as null | Sale
		};
	},
	props: {
		browseId: Number,
		controlsDisabled: Boolean,
		searchEnabled: Boolean
	},
	computed: {
		// eslint-disable-next-line max-lines-per-function
		dataSource(): IBrowseDataSource<Sale> {
			let elem = this;
			return {
				getFullElement: (el:Sale)=>SaleService.getSale(el.ID),
				getSettings: () => BrowseService.getBrowse(this.browseId),
				getElements: (params)=>SaleService.getPurchaseDocuments(params),
				getTotals(view:View):Promise<FetchedTotal[]>{
					return SaleService.getPurchaseDocumentTotals(view);
				},
				edit(sale: Sale) {
					elem.$router.push(`/purchase-documents/edit/${sale.ID}`);
				},
				canEdit(sale:Sale):boolean{
					return !sale.IsSigned;
				},
				async delete(sales: Sale[]) {
					let haveBanks = await SaleService.salesHaveBanks(sales);
					if (haveBanks) {
						let confirm = await elem.$confirmBuilder(elem.$ct("confirm-delete-with-payments")).setWarningTitle().show();
						if (!confirm) {
							return false;
						}
					}
					if (sales.length > 1){
						let job = await SaleService.delSales(sales);
						JobService.once(`job-stopped/${job.getID()}`, ()=>elem.removeDeletedSales());
						elem.$wf.notify(elem.$ct("document-delete-job-started"), "info", 3000, "info-circle");
						return false;
					}
					await SaleService.delSale(sales[0]);
					return true;
				},
				info(sale: Sale) {
					elem.$router.push(`/purchase-documents/info/${sale.ID}`);
				},
				addRoute(view:View){
					let newTypeID = view.MetaData.NewSaleTypeID;
					return `/purchase-documents/new?typeId=${newTypeID}`;
				},
				browseName: "Purchase Documents",
				modelName: "Sale",
				browsePermissions: ()=> UserConfigService.getPermissionSettings().SalePermissions,
				getOnElementUpdateSocketEvent: (item:Sale)=>`/sale/${item.ID}/update`,
				onSocketElementUpdated: (data:any)=>new Sale(data),
				extraFieldSettings: {
					getExtraFields: SaleService.getExtraFields.bind(SaleService),
					nameSettings: DossierConfigService.getExtraFieldNamesConfigs().SaleNames,
					batchEdit: SaleService.batchEditExtraField.bind(SaleService),
					edit: (SaleService.updateExtraFields as any).bind(SaleService)
				},
			};
		},
		hasUblModule():boolean{
			return AuthService.hasModule(MODULE.ELECTRONISCHE_FACTURATIE);
		},
		devModeItems():ISelectItem<string | Function>[] {
			let result:ISelectItem<string | Function>[] = [
				{
					text: "Document import referenties herstellen",
					value: this.fixImportedInDocument,
					icon: "wrench",
					disabled: this.selectedItems.length == 0
				}
			];
			return result;
		},
		peppolSendAction():ISelectItem<Function>{
			let disabled = false;
			let selectedItem = this.getSelectedItem(this.selectedContextMenuItem);
			if (!AuthService.hasModule(MODULE.ELECTRONISCHE_FACTURATIE)){
				disabled = true;
			}else if (this.selectedItems.length > 1) {
				disabled = true;
			}else if (!selectedItem || selectedItem.isSaldoDocument()) {
				disabled = true;
			}
			return {
				text: this.$ct("peppol-send"),
				value: this.sendViaPeppol,
				icon: "project-diagram",
				disabled,
			};
		},
		createSelfBillingPurchaseAction():ISelectItem<Function>{
			let disabled = false;
			let selectedItem = this.getSelectedItem(this.selectedContextMenuItem);
			if (!AuthService.hasModule(MODULE.ELECTRONISCHE_FACTURATIE)){
				disabled = true;
			}else if (this.selectedItems.length > 1) {
				disabled = true;
			}else if (!selectedItem || selectedItem.isSaldoDocument()) {
				disabled = true;
			}else if(selectedItem.LastSentViaPeppolDate == null) {
				disabled = true;
			}else if([14,15].indexOf(selectedItem.TypeID) == -1) {
				disabled = true;
			}
			return {
				text: this.$ct("peppol-create-self-billed-purchase"),
				value: this.createSelfBilledPurchase,
				icon: "store",
				disabled
			};
		},
		// eslint-disable-next-line max-lines-per-function
		singleItemActions():ISelectItem<string | Function>[]{
			let items:ISelectItem<string | Function>[] = [
				{
					text: this.$ct("print-sale"),
					value: "printSale",
					icon: "print"
				},
				{
					text: this.$ct("download-sale"),
					value: "downloadSale",
					icon: "download"
				},
				{
					text: this.$ct("email-sale"),
					value: "sendSale",
					icon: "envelope"
				}
			];
			if (AuthService.hasModule(MODULE.CLIENT_PORTAL)){
				items.push({
					text: this.$ct("email-sale-portal"),
					value: "sendPortal",
					icon: "folder-open"
				});
			}
			items.push(
				{
					text: this.$ct("process-payment").toString(),
					value: "processPayment",
					icon: "euro-sign"
				},
				{
					text: this.$ct("send-reminder"),
					icon: "history",
					value: "sendReminder"
				},
				{
					text: this.$ct("public-url-create").toString(),
					value: "createPublicLink",
					icon: "globe"
				}
			);
			if (AuthService.hasModule(MODULE.ELECTRONISCHE_FACTURATIE)){
				items.push({
						text: this.$ct("download-ubl"),
						value: "downloadUbl",
						icon: "file-invoice"
					},
					this.peppolSendAction,
				);
				if (AuthService.hasModule(MODULE.SELF_BILLING)) {
					items.push(this.createSelfBillingPurchaseAction);
				}
			}
			items.push({
				text: this.$ct("convert-to-credit-note"),
				value: "convertToCreditNote",
				icon: "money-check-alt"
			}, {
				text: this.$ct("save-as"),
				value: "saveAs",
				icon: "save"
			},);

			if (AuthService.isDevModeEnabled){
				items.push(...this.devModeItems);
			}
			return items;
		},
		buttonBarItems():ISelectItem<string | Function>[] {
			if (this.selectedItems.length == 1) {
				return this.singleItemActions;
			}
			let items:ISelectItem<string | Function>[] = [];
			if (AuthService.isDevModeEnabled){
				items.push(...this.devModeItems);
			}
			return items;
		},
		firstSelectedItem(): Sale {
			return this.selectedItems[0];
		},
		contextMenuItems(): ISelectItem<string | Function>[] {
			return this.singleItemActions;
		},
		selectedContactId(): number {
			if (!this.selectedItems[0]) {
				return 0;
			}
			return this.selectedItems[0].ContactID;
		},
	},
	watch: {
		"$route.path": {
			immediate: true,
			handler(){
				if (this.$route.path == "/purchase-documents") {
					ToolbarService.title = this.$ct("purchase-supplier");
				}
			}
		}
	},
	methods: {
		async fixImportedInDocument(item?:Sale){
			let items = this.selectedItems;
			if (item) {
				items = [item];
			}
			await SaleService.fixImportedInSales(items.map(i=>i.ID));
		},
		getSelectedItem(item?:Sale | null):Sale | undefined{
			if (item){
				return item;
			}
			if (this.firstSelectedItem){
				return this.firstSelectedItem;
			}
			return undefined;
		},
		processPayment(item?:Sale) {
			item = this.getSelectedItem(item);
			if (!item) return;
			this.quickBankEditSale = item;
		},
		sendReminder(item?: Sale) {
			item = this.getSelectedItem(item);
			if (!item) return;
			this.selectedItems.push(item);
			this.saleReminder = true;
		},
		cancelSelection(){
			if (!this.browseState) return;
			this.browseState.emptySelectedItems();
		},
		updateView() {
			if (!this.browseState) return;
			this.browseState.fetch();
		},
		closeSaleRemidner() {
			this.saleReminder = false;
			if (!this.browseState) return;
			this.browseState.fetch();
		},
		async createPublicLink(item:Sale) {
			this.publicLinkModal = true;
			this.publicLink = await SaleService.generatePublicUrl(item);
		},
		closePublicLink(){
			this.publicLinkModal = false;
			this.publicLink = "";
		},
		async downloadUbl(sale?:Sale){
			sale = this.getSelectedItem(sale);
			if (!sale) return;
			await UblService.downloadInvoiceUbl(this, sale);
		},
		async createUblArchive(){
			this.createUblArchiveModal = true;
		},

		async printSale(item?:Sale) {
			item = this.getSelectedItem(item);
			if (!item) return;
			let layoutID = item.LayoutID;
			if (layoutID == 0) {
				layoutID = DossierConfigService.getSaleConfig(item.TypeID).DefaultLayoutID;
				if (layoutID == 0) {
					this.$wf.notify(this.$ct("error-no-layout-selected"), "danger", 3000, "exclamation-triangle");
				}
			}
			await SaleService.printSale(item, layoutID);
		},
		async downloadSale(item?: Sale) {
			item = this.getSelectedItem(item);
			if (!item) return;
			await SaleService.downloadAndSaveSale(item, item.LayoutID);
		},

		async sendSale(item?: Sale) {
			item = this.getSelectedItem(item);
			if (!item) return;
			this.toSendSale = await SaleService.getSale(item.ID);
			this.sendModal = true;
		},

		async sendPortal(item?:Sale){
			item = this.getSelectedItem(item);
			if (!item) return;
			item = await SaleService.getSale(item.ID);
			this.toSendSale = item;
			this.sendPortalModal = true;
		},

		convertToCreditNote(){
			if (this.selectedItems.length == 0) return;
			this.convertToCreditNoteModal = true;
		},
		async doConvertToCreditNote(autoSettleCreditNote: boolean){
			try {
				let result = await SaleService.convertToCreditNote(this.selectedItems.map(s=>s.ID), autoSettleCreditNote);
				this.$wf.notify(this.$ctc("created-{amount}-creditnotes", result.length, {amount: result.length}), "success", 3000, "check");
			} finally {
				this.convertToCreditNoteModal = false;
			}
			this.updateView();
		},
		saveAs(sale:Sale) {
			if (this.selectedItems.length == 0){
				this.selectedItems.push(sale);
			}
			this.saveAsModal = true;
		},
		removeDeletedSales(){
			if (!this.browseState) return;
			this.browseState.fetch();
		},
		onColClick(result:string, value:Sale){
			if (result == "edit-delivery-date"){
				this.editDeliveryDateModalSale = value;
			}
		},
		async sendViaPeppol(item?:Sale){
			item = this.getSelectedItem(item);
			if (!item) return;
			let sale = await SaleService.getSale(item.ID);
			this.toSendSale = sale;
			this.peppolModalOpen = true;
		},
		async createSelfBilledPurchase(item?:Sale){
			if (!AuthService.hasModule(MODULE.SELF_BILLING)) return;
			item = this.getSelectedItem(item);
			if (!item) return;
			this.$router.push(`/purchase/from-selfbilled-purchase/${item.ID}`);
		}
	},
	created(){
		if (!checkHasModule(MODULE.AANKOOPFACTUREN, this.$router)){
			throw new Error("MISSING MODULE");
		}
	},
	components: {
		AppBrowse,
		AppBankEdit,
		AppSaleReminder,
		AppSendSale,
		AppPublicLink,
		AppSendPortal,
		AppCreateUblArchiveModal,
		AppSendViaPeppolModal,
		AppSaveAsModal,
		AppConvertToCreditNoteModal
	},
	i18nEx: {
		componentPrefix: "components.sales"
	}
});
